import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Copy, GitBranch, Plus, Trash2 } from 'lucide-react';
import { PageHeader, EmptyState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox, Select, Separator, Switch } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useDeletePipeline, usePipelines, useRequirableFields, useSavePipeline } from './api';
import type { Pipeline, PipelineEntityType, PipelineInput, StageType } from './types';

/**
 * The stage colours on offer. A fixed palette keeps the board legible: every
 * pipeline draws from the same handful of tints rather than a free-for-all.
 */
const PALETTE = [
  '#6b7280',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#f59e0b',
  '#14b8a6',
  '#10b981',
  '#ef4444',
] as const;

const STAGE_TYPE_LABELS: Record<StageType, string> = {
  OPEN: 'Open',
  WON: 'Won',
  LOST: 'Lost',
};

/**
 * Number fields are kept as strings while editing so a person can clear one
 * and retype it; they become numbers when the payload is built.
 */
type StageDraft = {
  id?: string;
  /** Stable React key — new stages have no id yet. */
  key: string;
  name: string;
  probability: string;
  colour: string;
  stageType: StageType;
  rottingDays: string;
  requiredFields: string[];
  recordCount: number;
};

type Draft = {
  id?: string;
  name: string;
  isDefault: boolean;
  stages: StageDraft[];
};

let nextKey = 0;
const freshKey = () => `new-${(nextKey += 1)}`;

function toDraft(pipeline: Pipeline): Draft {
  return {
    id: pipeline.id,
    name: pipeline.name,
    isDefault: pipeline.isDefault,
    stages: pipeline.stages.map((stage) => ({
      id: stage.id,
      key: stage.id,
      name: stage.name,
      probability: String(stage.probability),
      colour: stage.colour,
      stageType: stage.stageType,
      rottingDays: stage.rottingDays === null ? '' : String(stage.rottingDays),
      requiredFields: stage.requiredFields,
      recordCount: stage.recordCount ?? stage.dealCount ?? 0,
    })),
  };
}

function newStage(overrides: Partial<StageDraft> = {}): StageDraft {
  return {
    key: freshKey(),
    name: '',
    probability: '10',
    colour: PALETTE[1],
    stageType: 'OPEN',
    rottingDays: '',
    requiredFields: [],
    recordCount: 0,
    ...overrides,
  };
}

/** A new pipeline starts with the three stages every pipeline must have. */
function newDraft(): Draft {
  return {
    name: '',
    isDefault: false,
    stages: [
      newStage({ name: 'New', probability: '10' }),
      newStage({ name: 'Won', probability: '100', colour: '#10b981', stageType: 'WON' }),
      newStage({ name: 'Lost', probability: '0', colour: '#ef4444', stageType: 'LOST' }),
    ],
  };
}

/** The reason the draft cannot be saved yet, or null when it can. */
function validate(draft: Draft): string | null {
  if (draft.name.trim().length < 2) return 'Give the pipeline a name';
  if (draft.stages.length < 2) return 'A pipeline needs at least two stages';
  if (draft.stages.some((stage) => stage.name.trim() === '')) return 'Every stage needs a name';
  const names = draft.stages.map((stage) => stage.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return 'Stage names must be unique';
  const types = draft.stages.map((stage) => stage.stageType);
  if (!types.includes('OPEN')) return 'Add an open stage for new records to enter';
  if (!types.includes('WON')) return 'Mark one stage as won';
  if (!types.includes('LOST')) return 'Mark one stage as lost';
  return null;
}

/** "1 lead", "3 deals" — whichever this board holds. */
function noun(entityType: PipelineEntityType, count: number): string {
  const word = entityType === 'lead' ? 'lead' : 'deal';
  return `${word}${count === 1 ? '' : 's'}`;
}

function toPayload(draft: Draft, entityType: PipelineEntityType): PipelineInput {
  return {
    ...(draft.id ? { id: draft.id } : { entityType }),
    name: draft.name.trim(),
    isDefault: draft.isDefault,
    stages: draft.stages.map((stage) => ({
      ...(stage.id ? { id: stage.id } : {}),
      name: stage.name.trim(),
      probability: Math.min(100, Math.max(0, Math.round(Number(stage.probability) || 0))),
      colour: stage.colour,
      stageType: stage.stageType,
      rottingDays: stage.rottingDays === '' ? null : Math.max(1, Math.round(Number(stage.rottingDays))),
      requiredFields: stage.requiredFields,
    })),
  };
}

/** The server's message, with zod's field detail when that is more useful. */
function describe(caught: unknown, fallback: string): string {
  if (!(caught instanceof ApiRequestError)) return fallback;
  if (caught.details && caught.details.length > 0) {
    return caught.details.map((detail) => detail.message).join(' ');
  }
  return caught.message;
}

export function PipelinesPage() {
  // One editor, two kinds of board: /crm/pipelines/deal and /crm/pipelines/lead.
  const params = useParams<{ entityType?: string }>();
  const entityType: PipelineEntityType = params.entityType === 'lead' ? 'lead' : 'deal';
  const forLeads = entityType === 'lead';

  const { data: pipelines = [], isLoading } = usePipelines(entityType);
  const { data: requirableFields = [] } = useRequirableFields();
  const save = useSavePipeline();
  const remove = useDeletePipeline();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // "New pipeline" clears the selection on purpose. Without this flag the
  // first-pipeline auto-select below would refill the blank form at once.
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(newDraft);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Switching between the deal and lead boards is a different list entirely,
  // so the editor lets go of whatever it was holding.
  useEffect(() => {
    setSelectedId(null);
    setCreating(false);
    setDraft(newDraft());
    setError(null);
  }, [entityType]);

  // Select the first pipeline once the list arrives.
  useEffect(() => {
    if (!selectedId && !creating && pipelines.length > 0) {
      setSelectedId(pipelines[0].id);
      setDraft(toDraft(pipelines[0]));
    }
  }, [pipelines, selectedId, creating]);

  const selected = useMemo(
    () => pipelines.find((pipeline) => pipeline.id === selectedId) ?? null,
    [pipelines, selectedId],
  );

  const problem = validate(draft);

  const pick = (pipeline: Pipeline) => {
    setSelectedId(pipeline.id);
    setCreating(false);
    setDraft(toDraft(pipeline));
    setError(null);
  };

  const startNew = () => {
    setSelectedId(null);
    setCreating(true);
    setDraft(newDraft());
    setError(null);
  };

  /**
   * "Another one like this one" — usually what somebody means when they set
   * out to build a second pipeline. The copy keeps the stages and drops
   * everything that belongs to the original: its id, its default flag and the
   * records sitting in it.
   */
  const duplicate = () => {
    setSelectedId(null);
    setCreating(true);
    setError(null);
    setDraft({
      name: `${draft.name} copy`,
      isDefault: false,
      stages: draft.stages.map((stage) => ({
        ...stage,
        id: undefined,
        key: freshKey(),
        recordCount: 0,
      })),
    });
  };

  const patchStage = (key: string, changes: Partial<StageDraft>) => {
    setDraft({
      ...draft,
      stages: draft.stages.map((stage) => (stage.key === key ? { ...stage, ...changes } : stage)),
    });
  };

  // Won and lost have fixed odds; switching type sets them so nobody has to.
  const setStageType = (key: string, stageType: StageType) => {
    const probability = stageType === 'WON' ? '100' : stageType === 'LOST' ? '0' : undefined;
    patchStage(key, { stageType, ...(probability !== undefined ? { probability } : {}) });
  };

  const toggleRequired = (key: string, field: string) => {
    const stage = draft.stages.find((candidate) => candidate.key === key);
    if (!stage) return;
    const requiredFields = stage.requiredFields.includes(field)
      ? stage.requiredFields.filter((candidate) => candidate !== field)
      : [...stage.requiredFields, field];
    patchStage(key, { requiredFields });
  };

  const moveStage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draft.stages.length) return;
    const stages = [...draft.stages];
    [stages[index], stages[target]] = [stages[target], stages[index]];
    setDraft({ ...draft, stages });
  };

  const addStage = () => {
    // New stages slot in before the first closing stage, where work happens.
    const closingIndex = draft.stages.findIndex((stage) => stage.stageType !== 'OPEN');
    const stages = [...draft.stages];
    stages.splice(closingIndex === -1 ? stages.length : closingIndex, 0, newStage());
    setDraft({ ...draft, stages });
  };

  const removeStage = (key: string) => {
    setDraft({ ...draft, stages: draft.stages.filter((stage) => stage.key !== key) });
  };

  const onSave = async () => {
    setError(null);
    try {
      const saved = await save.mutateAsync(toPayload(draft, entityType));
      setSelectedId(saved.id);
      setCreating(false);
      setDraft(toDraft(saved));
    } catch (caught) {
      setError(describe(caught, 'Could not save this pipeline.'));
    }
  };

  const onDelete = async () => {
    if (!draft.id) return;
    setDeleteError(null);
    try {
      await remove.mutateAsync(draft.id);
      setConfirmingDelete(false);
      const next = pipelines.find((pipeline) => pipeline.id !== draft.id);
      if (next) pick(next);
      else startNew();
    } catch (caught) {
      setDeleteError(describe(caught, 'Could not delete this pipeline.'));
    }
  };

  const title = forLeads ? 'Lead pipeline setup' : 'Deal pipeline setup';

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={title}
          description={`The stages a ${forLeads ? 'lead' : 'deal'} moves through.`}
        />
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={title}
        description={
          forLeads
            ? 'The columns of the lead board — how an enquiry is chased. Each board needs somewhere to enter, a qualified stage and a dead-end stage.'
            : 'The stages a deal moves through. Each pipeline needs somewhere to enter, a won stage and a lost stage.'
        }
        actions={
          <Can permission="crm.pipeline.create">
            <Button onClick={startNew}>
              <Plus className="h-4 w-4" />
              New pipeline
            </Button>
          </Can>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[17rem_1fr]">
        <nav className="space-y-1">
          {/* The same button as the header's. It belongs here too: this is the
              list of pipelines, so it is where somebody looks to add one. */}
          <Can permission="crm.pipeline.create">
            <Button variant="outline" className="mb-2 w-full" onClick={startNew}>
              <Plus className="h-4 w-4" />
              New {forLeads ? 'lead' : 'deal'} pipeline
            </Button>
          </Can>

          {pipelines.map((pipeline) => (
            <button
              key={pipeline.id}
              onClick={() => pick(pipeline)}
              className={cn(
                'w-full rounded-md border px-3 py-2.5 text-start transition-colors',
                selectedId === pipeline.id ? 'border-primary bg-primary/5' : 'hover:bg-accent',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{pipeline.name}</span>
                {pipeline.isDefault && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    Default
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {pipeline.stages.length} {pipeline.stages.length === 1 ? 'stage' : 'stages'} ·{' '}
                {pipeline.recordCount} {noun(entityType, pipeline.recordCount)}
              </div>
            </button>
          ))}
          {creating && (
            <div className="rounded-md border border-primary bg-primary/5 px-3 py-2.5 text-sm font-medium">
              {draft.name.trim() || 'New pipeline'}
            </div>
          )}
          {pipelines.length === 0 && !creating && (
            <EmptyState
              icon={<GitBranch className="h-8 w-8" />}
              title="No pipelines yet"
              description={`Create your first pipeline to start tracking ${forLeads ? 'leads' : 'deals'}.`}
            />
          )}
        </nav>

        <Card className="p-5">
          {/* Which of the two things this form is doing, said out loud. */}
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-sm font-semibold">
              {creating ? `New ${forLeads ? 'lead' : 'deal'} pipeline` : (selected?.name ?? 'Pipeline')}
            </h2>
            {creating && (
              <Badge variant="secondary" className="text-[10px]">
                Not saved yet
              </Badge>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div className="space-y-1.5">
              <Label htmlFor="pipeline-name">Pipeline name</Label>
              <Input
                id="pipeline-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pipeline-default">Default pipeline</Label>
              <div className="flex h-9 items-center gap-2">
                <Switch
                  id="pipeline-default"
                  checked={draft.isDefault}
                  disabled={Boolean(selected?.isDefault)}
                  onCheckedChange={(isDefault) => setDraft({ ...draft, isDefault })}
                />
                <span className="text-xs text-muted-foreground">
                  {selected?.isDefault
                    ? 'Make another pipeline the default to change this'
                    : `New ${forLeads ? 'leads' : 'deals'} land here unless told otherwise`}
                </span>
              </div>
            </div>
          </div>

          <Separator className="my-5" />

          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Stages</h3>
              <p className="text-xs text-muted-foreground">
                In board order. A stage that holds deals can be renamed or moved, but not removed.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addStage}>
              <Plus className="h-4 w-4" />
              Add stage
            </Button>
          </div>

          <div className="scrollbar-thin overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Order</TableHead>
                  <TableHead className="min-w-[12rem]">Name</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="w-24">Odds %</TableHead>
                  <TableHead className="min-w-[13rem]">Colour</TableHead>
                  <TableHead className="w-28">Rots after</TableHead>
                  {/* Deal fields; a lead has none of them yet. */}
                  {!forLeads && <TableHead className="min-w-[16rem]">Required to leave</TableHead>}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.stages.map((stage, index) => (
                  <TableRow key={stage.key}>
                    <TableCell className="align-top">
                      <div className="flex items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === 0}
                          onClick={() => moveStage(index, -1)}
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === draft.stages.length - 1}
                          onClick={() => moveStage(index, 1)}
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        value={stage.name}
                        placeholder="Stage name"
                        className="h-8"
                        aria-label="Stage name"
                        onChange={(event) => patchStage(stage.key, { name: event.target.value })}
                      />
                      {stage.recordCount > 0 && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {stage.recordCount} {noun(entityType, stage.recordCount)} here
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={stage.stageType}
                        className="h-8"
                        aria-label="Stage type"
                        onChange={(event) => setStageType(stage.key, event.target.value as StageType)}
                      >
                        {(Object.keys(STAGE_TYPE_LABELS) as StageType[]).map((type) => (
                          <option key={type} value={type}>
                            {STAGE_TYPE_LABELS[type]}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={stage.probability}
                        className="h-8"
                        aria-label="Win probability"
                        onChange={(event) => patchStage(stage.key, { probability: event.target.value })}
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                        {PALETTE.map((colour) => (
                          <button
                            key={colour}
                            type="button"
                            aria-label={`Use ${colour}`}
                            aria-pressed={stage.colour === colour}
                            onClick={() => patchStage(stage.key, { colour })}
                            className={cn(
                              'h-5 w-5 rounded-full border-2 transition-transform',
                              stage.colour === colour
                                ? 'scale-110 border-foreground'
                                : 'border-transparent hover:scale-110',
                            )}
                            style={{ backgroundColor: colour }}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={stage.rottingDays}
                        placeholder="Never"
                        className="h-8"
                        aria-label="Days before a deal here is flagged as rotting"
                        disabled={stage.stageType !== 'OPEN'}
                        onChange={(event) => patchStage(stage.key, { rottingDays: event.target.value })}
                      />
                    </TableCell>
                    {!forLeads && (
                      <TableCell className="align-top">
                        <div className="flex flex-wrap gap-x-3 gap-y-1.5 pt-1.5">
                          {requirableFields.map((field) => (
                            <label
                              key={field.key}
                              className="flex cursor-pointer items-center gap-1.5 text-xs"
                            >
                              <Checkbox
                                checked={stage.requiredFields.includes(field.key)}
                                onCheckedChange={() => toggleRequired(stage.key, field.key)}
                              />
                              {field.label}
                            </label>
                          ))}
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="align-top">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={stage.recordCount > 0}
                        title={
                          stage.recordCount > 0
                            ? `Move the ${noun(entityType, 2)} out of this stage before removing it`
                            : 'Remove stage'
                        }
                        aria-label="Remove stage"
                        onClick={() => removeStage(stage.key)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4">
            <FormError message={error} />
          </div>

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <Can permission="crm.pipeline.delete">
                {draft.id ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeleteError(null);
                      setConfirmingDelete(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                    Delete pipeline
                  </Button>
                ) : null}
              </Can>
              <Can permission="crm.pipeline.create">
                {draft.id ? (
                  <Button variant="ghost" size="sm" onClick={duplicate}>
                    <Copy className="h-4 w-4" />
                    Duplicate
                  </Button>
                ) : null}
              </Can>
            </div>

            <div className="flex items-center gap-3">
              {problem && <span className="text-xs text-muted-foreground">{problem}</span>}
              <Can permission={draft.id ? 'crm.pipeline.update' : 'crm.pipeline.create'}>
                <Button onClick={onSave} disabled={save.isPending || problem !== null}>
                  {save.isPending ? 'Saving…' : draft.id ? 'Save changes' : 'Create pipeline'}
                </Button>
              </Can>
            </div>
          </div>

          {selected && (
            <p className="mt-3 text-xs text-muted-foreground">
              {selected.recordCount} {noun(entityType, selected.recordCount)}{' '}
              {selected.recordCount === 1 ? 'is' : 'are'} in this pipeline. Stage changes show on
              the board straight away.
            </p>
          )}
        </Card>
      </div>

      {confirmingDelete && selected && (
        <Dialog open onOpenChange={(open) => !open && setConfirmingDelete(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {selected.name}?</DialogTitle>
              <DialogDescription>
                The pipeline and its {selected.stages.length} stages will be removed. This only
                works while no deals are in it and it is not the default.
              </DialogDescription>
            </DialogHeader>
            <FormError message={deleteError} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={onDelete} disabled={remove.isPending}>
                {remove.isPending ? 'Deleting…' : 'Delete pipeline'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
